import { useState } from 'react';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_string,
  ImpersonationDTO,
  LoginTwoFactorDTO,
  LoginUserDTO,
  TokenExchangeDTO,
  AuthClient,
  ApiResponse_authDTO,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';
import {
  setImpersonationTokens,
  clearImpersonationTokens,
  setTokens,
} from '../utils/tokenUtils';

interface UseAuthApi {
  generateToken: (data: LoginUserDTO) => Promise<ApiResponse_string>;
  generateTokenTwoFactor: (data: LoginTwoFactorDTO,) => Promise<ApiResponse_string>;
  googleTokenExchange: (data: TokenExchangeDTO) => Promise<ApiResponse_string>;
  impersonateMemberLogin: (data: ImpersonationDTO, redirectPath?: string) => Promise<ApiResponse_string>;
  impersonateMemberLogout: (data: ImpersonationDTO,) => Promise<ApiResponse_string>;
  refreshUserToken: (refreshToken: string) => Promise<ApiResponse_authDTO>;
  isLoading: boolean;
  error: string | null;
}

export const useAuthApi = (): UseAuthApi => {
  const apiClient = new AuthClient(API_BASE_URL, axiosInstance);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const generateToken = async (
    data: LoginUserDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.generateToken(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error generating token', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const generateTokenTwoFactor = async (
    data: LoginTwoFactorDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.generateTokenTwoFactor(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error generating token with two factor', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const googleTokenExchange = async (
    data: TokenExchangeDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.googleTokenExchange(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error exchanging Google token', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateMemberLogin = async (
    data: ImpersonationDTO,
    redirectPath: string = '/admin/dashboard'
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      const response = await apiClient.impersonateMemberLogin(data);
      // Note: Backend actually returns ApiResponse<AuthDTO>, but API client types are wrong.
      // Cast to any to access the full response until API client is regenerated.
      const authResponse = response as any;
      
      // Store impersonated tokens in sessionStorage (tab-specific)
      if (authResponse.data?.accessToken && authResponse.data?.refreshToken) {
        setImpersonationTokens(
          authResponse.data.accessToken,
          authResponse.data.refreshToken,
          data.adminId ?? ''
        );
        
        // Only redirect if a redirectPath is explicitly provided
        // If empty, let the caller handle navigation (e.g., ImpersonationRedirect component)
        if (redirectPath) {
          window.location.href = redirectPath;
        }
      }
      
      return response;
    } catch (err: unknown) {
      handleHookError(err, 'Error impersonating member login', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateMemberLogout = async (
    data: ImpersonationDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      const response = await apiClient.impersonateMemberLogout(data);
      // Note: Backend actually returns ApiResponse<AuthDTO>, but API client types are wrong.
      // Cast to any to access the full response until API client is regenerated.
      const authResponse = response as any;
      
      // Clear impersonation and restore admin tokens
      clearImpersonationTokens(
        authResponse.data?.accessToken,
        authResponse.data?.refreshToken
      );
      
      // Immediately redirect to admin dashboard using window.location.href
      // This prevents any React re-rendering and flashing
      window.location.href = '/admin/dashboard';
      
      return response;
    } catch (err: unknown) {
      handleHookError(err, 'Error impersonating member logout', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUserToken = async (
    refreshToken: string,
  ): Promise<ApiResponse_authDTO> => {
    setIsLoading(true);
    try {
      const response = await apiClient.refreshToken({ refreshToken });
      if (response.data?.accessToken && response.data?.refreshToken) {
        setTokens(response.data.accessToken, response.data.refreshToken);
      }
      return response
    } catch (err: unknown) {
      handleHookError(err, 'Error refreshing token', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generateToken,
    generateTokenTwoFactor,
    googleTokenExchange,
    impersonateMemberLogin,
    impersonateMemberLogout,
    refreshUserToken,
    isLoading,
    error,
  };
};
